import { ApiAuthUser, ApiService, ApiSyncType } from "@incendium/api";
import { ArrowForward } from "@mui/icons-material";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import Bing_Ads_icon from "Assets/Images/ThirdParty/Bing_Ads_icon.svg";
import Google_Ads_icon from "Assets/Images/ThirdParty/Google_Ads_icon.svg";
import Meta_Ads_icon from "Assets/Images/ThirdParty/meta.png";
import Loading from "Components/Loading/Loading";
import WooCommerceOnboardingAdPlatformRow, {
  WooAdPlatformRowConfig,
} from "features/onboarding/woocommerce/WooCommerceOnboardingAdPlatformRow";
import WooCommerceOnboardingOtherPlatforms from "features/onboarding/woocommerce/WooCommerceOnboardingOtherPlatforms";
import { useSyncClients } from "Hooks/useSyncClients";

const AD_PLATFORMS: WooAdPlatformRowConfig[] = [
  {
    title: "Meta Ads",
    provider: "meta",
    service: ApiService.FACEBOOK,
    type: ApiSyncType.FACEBOOK_ADS,
    logo: Meta_Ads_icon,
  },
  {
    title: "Google Ads",
    provider: "google",
    service: ApiService.GOOGLE,
    type: ApiSyncType.GOOGLE_ADS,
    logo: Google_Ads_icon,
  },
  {
    title: "Bing Ads",
    provider: "bing",
    service: ApiService.BING,
    type: ApiSyncType.BING_ADS,
    logo: Bing_Ads_icon,
  },
];

interface IOnboardingAdPlatformsProps {
  onContinue: () => void;
  continueLabel?: string;
  onContinueWithInvite?: () => void;
  inactiveUsers?: ApiAuthUser[];
}

function OnboardingAdPlatforms({
  onContinue,
  continueLabel = "Continue",
  onContinueWithInvite,
  inactiveUsers = [],
}: IOnboardingAdPlatformsProps) {
  const { loading } = useSyncClients();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Loading />
      </Box>
    );
  }

  return (
    <Stack
      spacing={3}
      sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Ad platform connections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose the platforms you want to connect now. You can add or
              change these later.
            </Typography>
          </Box>
          <Alert severity="info" variant="outlined">
            Connecting an ad platform is a two-step process: sign in with your
            platform account, then expand the row to choose which ad account
            belongs to this project.
          </Alert>
          <Stack gap={1.5}>
            {AD_PLATFORMS.map((platform) => (
              <WooCommerceOnboardingAdPlatformRow
                key={platform.type}
                platform={platform}
              />
            ))}
            <WooCommerceOnboardingOtherPlatforms />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Other platforms lets you connect any additional ad sources not
            listed above using tracking parameters.
          </Typography>
        </Stack>
      </Box>
      <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          endIcon={<ArrowForward />}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
        {onContinueWithInvite && (
          <Stack spacing={1}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              size="large"
              onClick={onContinueWithInvite}
            >
              Approve & invite all inactive users
            </Button>
            {inactiveUsers.length > 0 && (
              <>
                <Typography variant="body2" color="error">
                  {inactiveUsers.length} inactive users found — invite them to
                  this project
                </Typography>
                {inactiveUsers.map((user) => (
                  <Typography variant="body2" color="error" key={user.id}>
                    {user.name} ({user.email})
                  </Typography>
                ))}
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default OnboardingAdPlatforms;
